/**
 * Phase 27.53.F — Reusable Notes + Email-to-Note panel.
 *
 * Shared UI for tickets and contract requests. Consumers supply a
 * scoped API object (list/add/updateVisibility/getInboundAddress) so
 * the panel can drive both endpoints without knowing which entity is
 * hosting it.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { User, Send, Copy, Reply, PencilLine } from 'lucide-react';
import ReplyComposeDialog from './ReplyComposeDialog';

export default function InboundNotesPanel({
  entityId,
  isKR,
  api,           // { listNotes, addNote, updateNoteVisibility, getInboundAddress, reply }
  title = 'Notes',
  inboundHint = 'CC or BCC to log an email as an internal note',
  testidPrefix = 'entity',
}) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newInternal, setNewInternal] = useState(false);
  const [inbound, setInbound] = useState(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replySeed, setReplySeed] = useState(null);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const r = await api.listNotes(entityId);
      setNotes(r.data || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [entityId, api]);

  useEffect(() => {
    if (!entityId) return;
    load();
    api.getInboundAddress(entityId).then((r) => setInbound(r.data)).catch(() => setInbound(null));
  }, [entityId, api, load]);

  const handleSubmit = async () => {
    if (!newNote.trim()) return;
    try {
      await api.addNote(entityId, newNote, newInternal ? 'internal' : 'visible');
      setNewNote('');
      setNewInternal(false);
      toast.success('Note added');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    }
  };

  const flipVisibility = async (note) => {
    const next = note.visibility === 'internal' ? 'visible' : 'internal';
    try {
      await api.updateNoteVisibility(entityId, note.note_id, next);
      toast.success(`Now ${next === 'internal' ? 'internal (KR only)' : 'visible to agent'}`);
      load();
    } catch {
      toast.error('Could not update visibility');
    }
  };

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-notes-panel`}>
      {inbound?.address && (
        <div className="bg-white border border-slate-200 rounded p-2 flex items-center justify-between gap-2" data-testid={`${testidPrefix}-inbound-address`}>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{inboundHint}</div>
            <div className="text-xs font-mono text-slate-800 truncate">{inbound.address}</div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {isKR && api.reply && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => { setReplySeed(null); setReplyOpen(true); }}
                data-testid={`${testidPrefix}-compose-btn`}
              >
                <PencilLine size={12} className="mr-1" /> Compose
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => {
                navigator.clipboard.writeText(inbound.address);
                toast.success('Copied');
              }}
              data-testid={`${testidPrefix}-copy-inbound`}
            >
              <Copy size={12} className="mr-1" /> Copy
            </Button>
          </div>
        </div>
      )}

      <ScrollArea className="max-h-72 border rounded-lg p-3">
        {loading ? (
          <p className="text-xs text-slate-400 text-center py-4">Loading notes…</p>
        ) : notes.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">No notes yet</p>
        ) : (
          <div className="space-y-3">
            {notes.map((n) => (
              <div
                key={n.note_id}
                className={`rounded-lg p-3 ${n.visibility === 'internal' ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}
                data-testid={`${testidPrefix}-note-${n.note_id}`}
              >
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <User size={14} className="text-slate-400" />
                  <span className="text-sm font-medium">{n.user_name}</span>
                  {n.source === 'email' && (
                    <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded uppercase">
                      📧 Email
                    </span>
                  )}
                  {n.source === 'email_out' && (
                    <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded uppercase">
                      📤 Reply Sent
                    </span>
                  )}
                  {n.visibility === 'internal' && (
                    <span className="text-[10px] font-semibold bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded uppercase">
                      🔒 Internal
                    </span>
                  )}
                  <span className="text-xs text-slate-400 ml-auto">
                    {new Date(n.created_at).toLocaleString()}
                  </span>
                  {isKR && n.source === 'email' && api.reply && (
                    <button
                      type="button"
                      onClick={() => { setReplySeed(n); setReplyOpen(true); }}
                      className="text-[10px] text-emerald-700 hover:text-emerald-900 underline flex items-center gap-0.5"
                      data-testid={`${testidPrefix}-reply-${n.note_id}`}
                    >
                      <Reply size={10} /> Reply
                    </button>
                  )}
                  {isKR && (
                    <button
                      type="button"
                      onClick={() => flipVisibility(n)}
                      className="text-[10px] text-slate-500 hover:text-slate-800 underline"
                      data-testid={`${testidPrefix}-flip-visibility-${n.note_id}`}
                    >
                      {n.visibility === 'internal' ? 'Make visible' : 'Make internal'}
                    </button>
                  )}
                </div>
                {(n.source === 'email' || n.source === 'email_out') && (n.from_email || n.to_email || n.subject) && (
                  <div className="text-[11px] text-slate-500 mb-1">
                    {n.from_email && <span>From: {n.from_email}</span>}
                    {n.to_email && <span>To: {n.to_email}</span>}
                    {(n.from_email || n.to_email) && n.subject && <span> · </span>}
                    {n.subject && <span>Subject: {n.subject}</span>}
                  </div>
                )}
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.content}</p>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      <div className="flex flex-col gap-2">
        <Textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Add a note… (Enter = new line · Cmd/Ctrl+Enter to send)"
          rows={3}
          className="resize-y min-h-[80px]"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          data-testid={`${testidPrefix}-new-note-input`}
        />
        <div className="flex items-center justify-between gap-2">
          {isKR ? (
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={newInternal}
                onChange={(e) => setNewInternal(e.target.checked)}
                className="rounded border-slate-300"
                data-testid={`${testidPrefix}-note-internal-toggle`}
              />
              🔒 Internal note (only KR sees this)
            </label>
          ) : <span />}
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!newNote.trim()}
            data-testid={`${testidPrefix}-note-send`}
          >
            <Send size={14} className="mr-1.5" /> Add Note
          </Button>
        </div>
      </div>

      {/* Phase 27.53.H — Reply From Hub compose dialog */}
      {api.reply && (
        <ReplyComposeDialog
          open={replyOpen}
          onOpenChange={setReplyOpen}
          seedNote={replySeed}
          sendReply={(payload) => api.reply(entityId, payload)}
          onSent={load}
          testidPrefix={`${testidPrefix}-reply`}
        />
      )}
    </div>
  );
}
